import { useRef } from 'react'

import { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { groupBy, orderBy } from 'lodash'

import { useEffect } from 'modules/core/hooks/useEffect'

import { CreationCard } from 'modules/cost_centers/components/CostCenterAssigments/components/Cards/CreationCard'
import { MutationCard } from 'modules/cost_centers/components/CostCenterAssigments/components/Cards/MutationCard'
import { Timeline } from 'modules/cost_centers/components/CostCenterAssigments/components/Timeline'
import { CostCenterAssignment } from 'modules/cost_centers/components/CostCenterAssigments/lib/types'

interface Props {
  assignments: CostCenterAssignment[]
  children?: React.ReactNode
  resourceId: number
  resourceType: string
  withAssignmentIdOpened?: number
  canManageAssignments: boolean
  onCreationCancel: () => void
  onDelete: () => void
  onSave: () => Promise<void>
  onToggleEdit: (value: boolean) => void
  refetchQueries?: TypedDocumentNode[]
}

export const Content = ({
  assignments,
  children,
  resourceId,
  resourceType,
  withAssignmentIdOpened,
  canManageAssignments,
  onCreationCancel,
  onDelete,
  onSave,
  onToggleEdit,
  refetchQueries,
}: Props) => {
  const groupedAssigments = Object.entries(
    orderBy(
      groupBy(
        assignments,
        (assignment) => `${assignment.startDate}-${assignment.endDate || 'null'}`
      ),
      (group) => group[0]?.startDate,
      'desc'
    )
  )

  const isFirstRender = useRef(true)

  // We want to check the first time the component is rendered. The reason is that
  // we're injecting new components in the timeline, and every time a new
  // component is injected, the component is rendered again.
  //
  // We're opening the current assignment when the sidepanel is opened by edit,
  // and this is preventing to reopened again when the component is re-rendered.
  useEffect(() => {
    isFirstRender.current = false
  }, [])

  if (groupedAssigments.length === 0 && canManageAssignments) {
    return (
      <CreationCard
        allAssignments={assignments}
        resourceId={resourceId}
        resourceType={resourceType}
        onCancel={onCreationCancel}
        onSave={onSave}
        refetchQueries={refetchQueries}
      />
    )
  }

  return (
    <Timeline>
      {children}

      {groupedAssigments.map(([date, assigmentsByDate]) => {
        return (
          <MutationCard
            key={date}
            assignments={assigmentsByDate}
            allAssignments={assignments}
            canManageAssignments={canManageAssignments}
            onSave={onSave}
            onDelete={onDelete}
            resourceId={resourceId}
            resourceType={resourceType}
            onToggleEdit={onToggleEdit}
            openOnRender={
              isFirstRender.current && withAssignmentIdOpened === assigmentsByDate[0]?.id
            }
            refetchQueries={refetchQueries}
          />
        )
      })}
    </Timeline>
  )
}
